import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { get, omit } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Frequency, Options, RRule, Weekday, rrulestr } from 'rrule';
import { CalendarInput } from './CalendarInput';
import './RRule.scss';
import { RadioButton } from './RadioButton';
import { Select, SelectOption } from './Select';
import { TextInput } from './TextInput';

export const daysOptions: SelectOption<Weekday>[] = [
  { value: RRule.SU, label: 'S' },
  { value: RRule.MO, label: 'M' },
  { value: RRule.TU, label: 'T' },
  { value: RRule.WE, label: 'W' },
  { value: RRule.TH, label: 'T' },
  { value: RRule.FR, label: 'F' },
  { value: RRule.SA, label: 'S' },
];

enum EndType {
  None,
  Date,
  Count,
}

const sanitizeRruleOptions = (options: Options): Partial<Options> => {
  const isMonthlyRecurrence = options.freq === RRule.MONTHLY;
  const isWeeklyRecurrence = options.freq === RRule.WEEKLY;
  return {
    ...omit(options, ['byhour', 'byminute', 'bysecond']),
    byweekday:
      isWeeklyRecurrence &&
      (!options.byweekday || (Array.isArray(options.byweekday) && !options.byweekday.length))
        ? [RRule.MO]
        : options.byweekday,
    bymonthday: isMonthlyRecurrence && options.dtstart ? options.dtstart.getDate() : [],
    bynmonthday: isMonthlyRecurrence ? options.bynmonthday : [],
  };
};

interface RRuleProps {
  value: string;
  minEndDate?: Dayjs;
  onChange: (value: string) => void;
  showYearFrequency?: boolean;
}

export const RRuleComponent = ({
  value,
  minEndDate,
  onChange,
  showYearFrequency = true,
}: RRuleProps) => {
  const options = useMemo(() => {
    try {
      return rrulestr(value).options;
    } catch {
      return new RRule().options;
    }
  }, [value]);

  const [interval, setIntervalR] = useState(options.interval.toString());

  useEffect(() => {
    setIntervalR(options.interval.toString());
  }, [options.interval]);

  const handleChangeInterval = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (!value) {
        setIntervalR(value);
        return;
      }

      const nextInterval = Number(value);

      if (Number.isNaN(nextInterval) || nextInterval < 1 || nextInterval > 365) return;

      setIntervalR(value);
      const newRule = new RRule({ ...sanitizeRruleOptions(options), interval: nextInterval });

      console.log('interval changed', value, nextInterval, newRule.toString());
      onChange(newRule.toString());
    },
    [onChange, options],
  );

  const handleIntervalInputBlur = useCallback(() => {
    const numericValue = +interval;
    const value = numericValue < 0 || !numericValue ? 1 : numericValue;

    setIntervalR(value.toString());
    const newRule = new RRule({ ...sanitizeRruleOptions(options), interval: value });

    onChange(newRule.toString());
  }, [interval, onChange, options]);

  const handleChangeFrequency = useCallback(
    (freq: number | null) => {
      if (typeof freq !== 'number') return;
      const newRule = new RRule(sanitizeRruleOptions({ ...options, freq }));

      onChange(newRule.toString());
    },
    [onChange, options],
  );

  const handleToggleDay = useCallback(
    (day: Weekday) => {
      const newRule = new RRule({
        ...sanitizeRruleOptions(options),
        byweekday:
          (!options.byweekday && [day.weekday]) ||
          (options.byweekday.includes(day.weekday)
            ? options.byweekday.filter(d => d !== day.weekday)
            : [...options.byweekday, day.weekday]),
      });

      onChange(newRule.toString());
    },
    [onChange, options],
  );

  const handleChangeEndType = useCallback(
    (type: EndType) => {
      if (type === EndType.None) {
        const newRule = new RRule(omit(sanitizeRruleOptions(options), ['until', 'count']));
        onChange(newRule.toString());
      }
      if (type === EndType.Date) {
        const newRule = new RRule(
          omit(
            {
              ...sanitizeRruleOptions(options),
              until: minEndDate ? minEndDate.add(2, 'd').toDate() : new Date(),
            },
            ['count'],
          ),
        );
        onChange(newRule.toString());
      }
      if (type === EndType.Count) {
        const newRule = new RRule(omit({ ...sanitizeRruleOptions(options), count: 10 }, ['until']));
        onChange(newRule.toString());
      }
    },
    [minEndDate, onChange, options],
  );

  const selectedEndType = useMemo(() => {
    if (typeof options.count === 'number') return EndType.Count;
    if (options.until) return EndType.Date;

    return EndType.None;
  }, [options.count, options.until]);

  const handleChangeUntilDate = useCallback(
    (date: Dayjs) => {
      const newRule = new RRule({ ...sanitizeRruleOptions(options), until: date.toDate() });
      onChange(newRule.toString());
    },
    [onChange, options],
  );

  const handleChangeCount = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const count = +e.target.value;
      if (isNaN(count) || count > 365) return;

      const newRule = new RRule({ ...sanitizeRruleOptions(options), count });
      onChange(newRule.toString());
    },
    [onChange, options],
  );

  const handleSelectQuarter = useCallback(() => {
    setIntervalR('3');

    const newRule = new RRule(
      sanitizeRruleOptions({ ...options, freq: RRule.MONTHLY, interval: 3 }),
    );
    onChange(newRule.toString());
  }, [onChange, options]);

  const handleSelectYear = useCallback(() => {
    setIntervalR('1');

    const newRule = new RRule(
      sanitizeRruleOptions({ ...options, freq: RRule.YEARLY, interval: 1 }),
    );
    onChange(newRule.toString());
  }, [onChange, options]);

  const repeatOptions: SelectOption<number>[] = useMemo(() => {
    const items: SelectOption<number>[] = [
      { label: `Day${interval === '1' ? '' : 's'}`, value: RRule.DAILY },
      { label: `Week${interval === '1' ? '' : 's'}`, value: RRule.WEEKLY },
      { label: `Month${interval === '1' ? '' : 's'}`, value: RRule.MONTHLY },
    ];

    if (showYearFrequency) {
      items.push({ label: `Year${interval === '1' ? '' : 's'}`, value: RRule.YEARLY });
    }

    return items;
  }, [interval, showYearFrequency]);

  const endOptions = useMemo(
    () => [
      { value: EndType.None, label: 'Never' },
      {
        value: EndType.Date,
        label: (
          <div className="flex gap-2 items-center">
            <div className="w-[55px] shrink-0 self-center">On</div>
            <CalendarInput
              allowClear={false}
              disabled={selectedEndType !== EndType.Date}
              value={options.until ? dayjs(options.until) : null}
              disabledDate={date => (minEndDate ? date.isBefore(minEndDate) : false)}
              onChange={handleChangeUntilDate}
              className={classNames(
                '[&>div]:w-[196px] [&>div]:min-h-8 [&>div]:py-0 [&_.ant-picker-suffix]:absolute [&_.ant-picker-suffix]:right-0',
                {
                  '[&_*]:!cursor-pointer': selectedEndType === EndType.Date,
                  '[&>div]:!bg-[#fff]': selectedEndType !== EndType.Date,
                },
              )}
            />
          </div>
        ),
      },
      // {
      //   value: EndType.Count,
      //   label: (
      //     <div className="flex gap-2 items-center">
      //       <div className="w-[55px] shrink-0 self-center">After</div>
      //       <div
      //         className={classNames('flex items-center gap-4', {
      //           'text-[#A3A3A3] rounded overflow-hidden': selectedEndType !== EndType.Count,
      //         })}
      //       >
      //         <TextInput
      //           name=""
      //           type="number"
      //           disabled={selectedEndType !== EndType.Count}
      //           value={selectedEndType === EndType.Count ? options.count || '' : 10}
      //           onChange={handleChangeCount}
      //           className={classNames('!min-h-8 w-[60px] min-w-[60px] py-0 overflow-hidden', {
      //             'text-[#A3A3A3] !border-[#d9d9d9]': selectedEndType !== EndType.Count,
      //           })}
      //         />
      //         <div>occurrence{options.count !== 1 && 's'}</div>
      //       </div>
      //     </div>
      //   ),
      // },
    ],
    [
      handleChangeCount,
      handleChangeUntilDate,
      minEndDate,
      // options.count,
      options.until,
      selectedEndType,
    ],
  );
  return (
    <div>
      <div className="flex justify-between text-[10px] mb-2">
        <div>REPEAT OPTION</div>
        <div className="flex items-center justify-end gap-2 text-right divide-x">
          <div
            className="text-blue-700 hover:underline cursor-pointer"
            onClick={handleSelectQuarter}
          >
            QUARTER
          </div>
          {showYearFrequency && (
            <div
              className="text-blue-700 hover:underline cursor-pointer pl-2"
              onClick={handleSelectYear}
            >
              YEAR
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-4 items-center flex-wrap">
        <div className="flex gap-4 items-center">
          <span className="text-gray-900 text-sm">Repeat every</span>
          <TextInput
            name=""
            type="number"
            value={interval}
            onBlur={handleIntervalInputBlur}
            className="w-[60px] min-w-[60px] !min-h-8 py-0"
            onChange={handleChangeInterval}
          />
        </div>
        <div
          className={classNames(
            '[&>div>div[class$="control"]]:w-[120px] [&_div[class$="indicatorContainer"]]:pt-1.5',
            '[&>div>div]:min-h-8 [&_div[class$="indicatorContainer"]]:h-8 [&_div[class$="control"]]:pl-2 [&_div[class$="control"]]:w-24 [&_div[class$="control"]]:h-8',
          )}
        >
          <Select
            value={get(options, 'freq', 0)}
            options={repeatOptions}
            onChange={handleChangeFrequency}
          />
        </div>
      </div>
      {options?.freq !== Frequency.DAILY && (
        <>
          <p className="text-gray-900 mt-6 mb-4 text-sm">Repeat on</p>
          <div className="flex gap-2">
            {daysOptions.map(d => {
              const isActive = options.byweekday && options.byweekday.includes(d.value.weekday);
              return (
                <div
                  key={d.value.toString()}
                  className={classNames(
                    'flex items-center justify-center w-9 h-9 text-sm rounded-lg cursor-pointer border border-[E3E3E3]',
                    {
                      'bg-[#999] hover:bg-[#777] text-white': isActive,
                      'hover:bg-[#AAA] text-gray-800 hover:text-white': !isActive,
                    },
                  )}
                  onClick={handleToggleDay.bind(null, d.value)}
                >
                  {d.label}
                </div>
              );
            })}
          </div>
        </>
      )}
      <p className="text-gray-900 text-sm mt-6 mb-4">Ends</p>
      <RadioButton
        className="space-y-4"
        buttonClassName="radio-button"
        items={endOptions}
        value={selectedEndType}
        onChange={handleChangeEndType}
      />
    </div>
  );
};
